import EmblaCarousel, { EmblaCarouselType } from 'embla-carousel'
import BaseComponent from '@/components/base'
import A11yStatus from '@/components/a11y/a11yStatus'

const selectors = {
  buttonNext: 'button[data-next]',
  buttonPrevious: 'button[data-previous]',
  pagination: '[data-pagination]',
}

export default class ProductDetailGallery extends BaseComponent {
  static TYPE = 'product-detail-gallery'

  color: string
  productTitle: string
  emblaNode: HTMLElement | undefined
  emblaViewport: HTMLElement | undefined
  emblaContainer: HTMLElement | undefined
  emblaPaginationNode: HTMLElement | undefined
  slides: HTMLElement[]
  listItems: HTMLElement[]
  pagination: HTMLElement | undefined
  buttonNext: HTMLElement | undefined
  buttonPrevious: HTMLElement | undefined
  slideshowDisabled = false
  emblaA11yStatus: A11yStatus | undefined
  emblaApi: EmblaCarouselType | undefined

  constructor(el: HTMLElement) {
    super(el)

    this.color = this.dataset.color ?? ''
    this.productTitle = this.dataset.productTitle ?? ''

    this.emblaNode = this.qs('.embla')
    this.emblaViewport = this.qs('.embla__viewport')
    this.emblaContainer = this.qs('.embla__container')
    this.emblaPaginationNode = this.qs('.embla__pagination')
    this.slides = this.qsa('.embla__slide')
    this.listItems = this.qsa('.product-detail-gallery__item')

    if (!this.emblaNode) {
      console.warn('ProductDetailGallery: Embla node not found')
      return
    }

    this.pagination = this.qs(selectors.pagination)
    this.buttonNext = this.qs(selectors.buttonNext)
    this.buttonPrevious = this.qs(selectors.buttonPrevious)

    this.slideshowDisabled = this.slideCount <= 1

    this.emblaA11yStatus = A11yStatus.generate(this.emblaNode)

    this.emblaApi = EmblaCarousel(this.emblaViewport as HTMLElement, {
      loop: this.slideCount > 1,
      watchDrag: !this.slideshowDisabled,
    })

    const setCurrentStatus = () => {
      this.updatePagination()
      this.updateCurrentStatus()
    }

    this.emblaApi.on('init', setCurrentStatus)
    this.emblaApi.on('reInit', setCurrentStatus)
    this.emblaApi.on('select', setCurrentStatus)

    this.buttonNext?.addEventListener('click', this.onButtonNextClick.bind(this))
    this.buttonPrevious?.addEventListener('click', this.onButtonPreviousClick.bind(this))
  }

  get isActive() {
    return this.el.getAttribute('aria-current') === 'true'
  }

  set isActive(value: boolean) {
    value ? this.el.setAttribute('aria-current', 'true') : this.el.removeAttribute('aria-current')
  }

  get activeIndex() {
    return this.emblaApi?.selectedScrollSnap() ?? 0
  }

  get slideCount() {
    return this.slides?.length ?? 0
  }

  destroy() {
    this.emblaApi?.destroy()

    super.destroy()
  }

  activate() {
    if (this.isActive) return

    (this.qsa('img') as HTMLImageElement[]).forEach(img => img.setAttribute('loading', 'eager'))

    this.el.setAttribute('aria-current', 'true')
    this.emblaApi?.reInit()
    this.isActive = true
  }

  deactivate() {
    this.el.removeAttribute('aria-current')
    this.isActive = false
  }

  updatePagination() {
    if (!this.pagination) return
    
    this.pagination.innerHTML = `${(this.emblaApi?.selectedScrollSnap() ?? 0) + 1} / ${this.emblaApi?.scrollSnapList().length ?? 0}`
  }

  updateAriaCurrent(items: HTMLElement[], activeIndex: number) {
    items?.forEach((item, index) => {
      if (index === activeIndex) {
        item.setAttribute('aria-current', 'true')
      }
      else {
        item.removeAttribute('aria-current')
      }
    })
  }

  updateCurrentStatus() {
    let msg = `Image ${this.activeIndex + 1} of ${this.slideCount} for ${this.productTitle}`

    if (this.color) {
      msg = `${msg} in ${this.color}`
    }

    if (this.emblaA11yStatus) this.emblaA11yStatus.text = msg
    this.updateAriaCurrent(this.slides, this.activeIndex)
  }

  filterByColor(color: string) {
    const target = (color || '').trim().toLowerCase()

    // Visible when no colour is selected, the media has no colour (e.g. videos),
    // or the media's colour matches the selected one.
    const isVisible = (el: HTMLElement) => {
      const c = (el.dataset.color || '').trim().toLowerCase()
      return !target || !c || c === target
    }

    // Stacked list (desktop + mobile) — toggle display, and tag the first visible
    // image as the hero (fills one screen, cover-cropped) so no second image peeks.
    let heroAssigned = false
    this.listItems.forEach(item => {
      const visible = isVisible(item)
      item.style.setProperty('display', visible ? '' : 'none')

      if (visible && !heroAssigned) {
        item.classList.add('is-hero')
        heroAssigned = true
      }
      else {
        item.classList.remove('is-hero')
      }
    })

    // Mobile slideshow — physically rebuild the container with only the matching
    // slides. embla mismeasures display:none slides (especially in loop mode),
    // which left two half-slides visible when switching colour.
    if (this.emblaContainer) {
      const visibleSlides = this.slides.filter(isVisible)

      this.slides.forEach(slide => slide.remove())
      visibleSlides.forEach(slide => this.emblaContainer!.appendChild(slide))

      this.emblaApi?.reInit({
        loop: visibleSlides.length > 1,
        watchDrag: visibleSlides.length > 1,
      })
      this.emblaApi?.scrollTo(0, false)
    }
  }

  onButtonNextClick(e: MouseEvent) {
    e.preventDefault()
    this.emblaApi?.scrollNext()
  }

  onButtonPreviousClick(e: MouseEvent) {
    e.preventDefault()
    this.emblaApi?.scrollPrev()
  }
}
